import React, { type ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import styled from '@emotion/styled';

import Link from '@atlaskit/link/link';
import { Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message/message';
import { ActionSubject } from '@atlassian/proforma-utils/analytics/events';

import { type ErrorUtils } from '../utils/ErrorUtils';

/**
 * Error messages that should be tagged with 'excludedErrors' instead of 'errorBoundary'
 * so they can be filtered out of metrics.
 */
const EXCLUDED_ERROR_MESSAGES: string[] = [
	"Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node.",
];

interface ErrorBoundaryProps {
	children?: ReactNode;
	errorUtils: ErrorUtils;
	renderError?: (props: { error: Error | null }) => ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
	errorInfo: string | null;
	error: Error | null;
}

// eslint-disable-next-line @repo/internal/react/no-class-components
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: any) {
		super(props);
		this.state = {
			hasError: false,
			errorInfo: null,
			error: null,
		};
	}

	componentDidCatch(error: any, errorInfo: any): void {
		this.setState({
			hasError: true,
			error,
			errorInfo: errorInfo.componentStack,
		});

		const isExcludedError =
			error instanceof Error &&
			EXCLUDED_ERROR_MESSAGES.some((message) => error.message === message);

		this.props.errorUtils.reportError(error, {
			actionSubject: ActionSubject.PROFORMA_UI,
			id: isExcludedError ? 'excludedErrors' : 'errorBoundary',
			errorInfo,
		});
	}

	render(): React.ReactNode {
		if (this.state.hasError) {
			// Render error UI here
			const { renderError } = this.props;
			return renderError ? (
				renderError({
					error: this.state.error,
				})
			) : (
				<ErrorPage>
					<ErrorPageSection>
						<SectionMessage appearance="error" title="Something went wrong">
							{/* eslint-disable @atlassian/i18n/no-literal-string-in-jsx */}
							<Text as="p">
								Refresh the page and try again. <br />
								{/* eslint-enable @atlassian/i18n/no-literal-string-in-jsx */}
								If the error persists,{' '}
								<Link
									href="https://support.thinktilt.com"
									target="_blank"
									rel="noopener noreferrer"
									/* eslint-disable @atlassian/i18n/no-literal-string-in-jsx */
								>
									contact support
									{/* eslint-enable @atlassian/i18n/no-literal-string-in-jsx */}
								</Link>
								.
							</Text>
						</SectionMessage>
					</ErrorPageSection>
				</ErrorPage>
			);
		}

		return this.props.children;
	}
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorPage = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-empty-styled-expression -- Ignored via go/DSP-18766
const ErrorPageSection = styled.div({});
